import { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@trillion/ui/components/ui/card";
import { Button } from "@trillion/ui/components/ui/button";
import { Badge } from "@trillion/ui/components/ui/badge";
import { Separator } from "@trillion/ui/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@trillion/ui/components/ui/select";
import {
  CheckCircle2,
  XCircle,
  Mail,
  MailPlus,
  MailCheck,
  User as UserIcon,
  Calendar,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { UserStatus } from "@trillion/enums";
import { adminApi } from "@/lib/admin-api";
import type { User, UserUpdateData } from "@/types";

// Keyed by the enum, so adding a member is a compile error until the label + colors are chosen.
const statusConfig: Record<UserStatus, { label: string; className: string }> = {
  [UserStatus.Active]: {
    label: "Active",
    className: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400",
  },
  [UserStatus.Invited]: {
    label: "Invited",
    className: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
  },
  [UserStatus.Suspended]: {
    label: "Suspended",
    className: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
  },
  [UserStatus.Deleted]: {
    label: "Deleted",
    className: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  },
};

interface UserDetailProps {
  user: User;
  onUserUpdated: () => void;
}

export function UserDetail({ user, onUserUpdated }: UserDetailProps) {
  const [status, setStatus] = useState<UserStatus>(user.user_status_id);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isResendingInvite, setIsResendingInvite] = useState(false);
  const [isResendingVerification, setIsResendingVerification] = useState(false);

  async function handleStatusUpdate() {
    if (status === user.user_status_id) return;

    setIsUpdating(true);
    try {
      await adminApi.users.update(user.user_id, {
        // The dropdown offers Active/Suspended/Deleted — Invited is set by the invite flow, never here.
        user_status_id: status as UserUpdateData["user_status_id"],
      });
      // Go through the label map: a numeric enum interpolates as its INTEGER, so `${status}` would
      // render "updated to 2." to the admin.
      toast.success(`User status updated to ${statusConfig[status].label}.`);
      onUserUpdated();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to update user status."
      );
    } finally {
      setIsUpdating(false);
    }
  }

  // Same enablement rules as the grid's row menu, mirroring the API's 409 gates.
  const canResendInvite = user.user_status_id === UserStatus.Invited;
  const canResendVerification =
    user.user_status_id === UserStatus.Active && !user.email_verified;

  async function handleResendInvite() {
    setIsResendingInvite(true);
    try {
      await adminApi.users.resendInvite(user.user_id);
      toast.success(`Invite re-sent to ${user.email}.`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to resend the invite.");
    } finally {
      setIsResendingInvite(false);
    }
  }

  async function handleResendVerification() {
    setIsResendingVerification(true);
    try {
      await adminApi.users.resendVerification(user.user_id);
      toast.success(`Verification email sent to ${user.email}.`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to send the verification email.");
    } finally {
      setIsResendingVerification(false);
    }
  }

  const variant = statusConfig[user.user_status_id] ?? statusConfig[UserStatus.Active];
  const createdDate = new Date(user.created_date);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>User Profile</span>
          <Badge variant="outline" className={variant.className}>
            {variant.label}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        {/* User Info */}
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex items-center gap-3">
            <Mail className="size-4 text-muted-foreground" />
            <div>
              <p className="text-xs text-muted-foreground">Email</p>
              <p className="text-sm font-medium">{user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <UserIcon className="size-4 text-muted-foreground" />
            <div>
              <p className="text-xs text-muted-foreground">Display Name</p>
              <p className="text-sm font-medium">
                {user.display_name || "-"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {user.email_verified ? (
              <CheckCircle2 className="size-4 text-emerald-600" />
            ) : (
              <XCircle className="size-4 text-muted-foreground" />
            )}
            <div>
              <p className="text-xs text-muted-foreground">Email Verified</p>
              <p className="text-sm font-medium">
                {user.email_verified ? "Yes" : "No"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Calendar className="size-4 text-muted-foreground" />
            <div>
              <p className="text-xs text-muted-foreground">Created</p>
              <p className="text-sm font-medium">
                {createdDate.toLocaleDateString()} {createdDate.toLocaleTimeString()}
              </p>
            </div>
          </div>
        </div>

        <Separator />

        {/* Status Management */}
        <div>
          <h3 className="mb-3 text-sm font-semibold">Status Management</h3>
          <div className="flex items-end gap-3">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs text-muted-foreground">
                Change Status
              </label>
              {/* Radix Select is string-valued, so the enum↔string conversion stays at this boundary. */}
              <Select
                value={String(status)}
                onValueChange={(v) => setStatus(Number(v) as UserStatus)}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={String(UserStatus.Active)}>Active</SelectItem>
                  <SelectItem value={String(UserStatus.Suspended)}>Suspended</SelectItem>
                  <SelectItem value={String(UserStatus.Deleted)}>Deleted</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={handleStatusUpdate}
              disabled={status === user.user_status_id || isUpdating}
              size="sm"
            >
              {isUpdating && <Loader2 className="mr-1 size-4 animate-spin" />}
              Update Status
            </Button>
          </div>
        </div>

        <Separator />

        {/* Email actions — neither changes anything this card renders, so no onUserUpdated() refetch. */}
        <div>
          <h3 className="mb-3 text-sm font-semibold">Email actions</h3>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleResendInvite}
              disabled={!canResendInvite || isResendingInvite}
            >
              {isResendingInvite ? (
                <Loader2 className="mr-1 size-4 animate-spin" />
              ) : (
                <MailPlus className="mr-1 size-4" />
              )}
              Resend invite
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleResendVerification}
              disabled={!canResendVerification || isResendingVerification}
            >
              {isResendingVerification ? (
                <Loader2 className="mr-1 size-4 animate-spin" />
              ) : (
                <MailCheck className="mr-1 size-4" />
              )}
              Send verification email
            </Button>
          </div>
        </div>

        {/* User ID */}
        <div className="text-xs text-muted-foreground">
          User ID: <code className="rounded bg-muted px-1 py-0.5">{user.user_id}</code>
        </div>
      </CardContent>
    </Card>
  );
}
